// ─── Talking to the server ───────────────────────────────────────────────────
//
// Adapted from Crafting Comb's own client/api.ts — the transport (`createApiClient`
// from `@nulife/app-ui`), the wall-clock conversion discipline, and most route
// shapes are reused, not reinvented. The one real difference: NuLife PA has
// no public customer-booking surface baked into this app (the public demo
// page is a separate static page, `/demo/:slug`, outside this React client
// entirely) — so there is no `/settings`-before-sign-in split here. `/me`
// already carries the organisation's timeZone/theme fields, which is enough.
//
// Dates cross the wire as ISO strings, parsed/formatted in this one file —
// every component downstream only ever sees a real `Date`, already converted
// onto the business's own clock (`toZonedWallClock`/`fromZonedWallClock`).

import { toZonedWallClock, fromZonedWallClock } from "@nulife/booking-ui";
import type { CalendarPort } from "@nulife/calendar-ui";
import type { CalendarItem } from "@nulife/calendar";
import { createApiClient, ApiError } from "@nulife/app-ui";

const client = createApiClient();
const { request, post, send } = client;

export const whenSignedOut = client.whenSignedOut;
export { ApiError };

// ─── Signing in / who am I ──────────────────────────────────────────────────

export type Me = {
  signedIn: boolean;
  name?: string;
  email?: string | null;
  role?: "Owner/Admin" | "Staff";
  canConfigure?: boolean;
  businessName?: string | null;
  timeZone?: string;
  organisationSlug?: string | null;
  /** Which personal-assistant modules this organisation is switched on for. */
  modules?: string[];
  /** A friend or relative let in by an access link: Messages only. */
  isGuest?: boolean;
  /** Whether the Ask Mira assistant is actually wired in. When false the bar
   *  is not drawn at all — a "not connected" placeholder helps nobody. */
  assistantAvailable?: boolean;
};

type RawMe = {
  session: { name: string; email: string | null; adminLevel: number; accessKind?: "member" | "guest" } | null;
  organisation: { name: string; timeZone: string; slug: string } | null;
  modules?: string[];
  assistantAvailable?: boolean;
};

export async function getMe(): Promise<Me> {
  try {
    const raw = await request<RawMe>("/me");
    if (!raw.session) return { signedIn: false };
    return {
      signedIn: true,
      name: raw.session.name,
      email: raw.session.email,
      role: raw.session.adminLevel > 0 ? "Owner/Admin" : "Staff",
      canConfigure: raw.session.adminLevel > 0,
      businessName: raw.organisation?.name ?? null,
      timeZone: raw.organisation?.timeZone ?? "Europe/London",
      organisationSlug: raw.organisation?.slug ?? null,
      modules: raw.modules ?? [],
      isGuest: raw.session.accessKind === "guest",
      assistantAvailable: raw.assistantAvailable === true,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return { signedIn: false };
    throw err;
  }
}

export const signIn = (email: string, password: string) =>
  post<{ session: unknown }>("/sign-in", { email, password });

/** Whose link this is, WITHOUT using it. A read: safe to repeat, safe for a
 *  link preview to trigger. */
export const previewInvite = (token: string) =>
  request<{ name: string; organisationName: string | null }>(
    `/invite-preview?token=${encodeURIComponent(token)}`);

/** Spend the link and get a session. Only ever from a deliberate press. */
export const acceptInvite = (token: string) =>
  post<{ name: string }>("/accept-invite", { token });

/** Give a contact a sign-in link. `reissue` is how the owner confirms she means
 *  to replace a link that already exists (the server answers 409 otherwise). */
export const issueAccessLink = (personId: string, reissue = false) =>
  post<{ url: string; fullName: string; reissued: boolean }>(
    `/people/${encodeURIComponent(personId)}/access-link`, { reissue });

export const signOut = () => post<{ ok: true }>("/sign-out", {});

// ─── Services ────────────────────────────────────────────────────────────────

export type Service = {
  id: string;
  name: string;
  durationMinutes: number;
  description: string | null;
  priceMinor: number | null;
  currency: string | null;
};

export const getServices = () => request<Service[]>("/services");

export const addService = (draft: {
  name: string; durationMinutes: number; description?: string; priceMinor?: number | null;
}) => post<{ id: string }>("/services", draft);

export const saveService = (id: string, patch: {
  name: string; durationMinutes: number; priceMinor: number | null;
}) =>
  request<{ id: string }>(`/services/${encodeURIComponent(id)}`, {
    method: "PATCH", body: JSON.stringify(patch),
  });

export const removeService = (id: string) =>
  request<{ changed: boolean }>(`/services/${id}`, { method: "DELETE" });

// ─── Hours ──────────────────────────────────────────────────────────────────

export type HoursRule = { weekday: number; startMinute: number; endMinute: number };

export const getHours = () => request<HoursRule[]>("/hours");

export type MonthDayFacts = { date: string; working: boolean; openSlots: number; bookings?: number };

export const getMonth = (serviceId: string, year: number, monthIndex: number) =>
  request<MonthDayFacts[]>(
    `/month?${new URLSearchParams({ serviceId, year: String(year), month: String(monthIndex) })}`,
  );

export const saveHours = (hours: { weekday: number; open: boolean; from: string; to: string }[]) =>
  request<{ ok: true }>("/hours", { method: "PUT", body: JSON.stringify({ hours }) });

// ─── Website knowledge (Tier 1 self-serve) ─────────────────────────────────

export type KnowledgeSource = { id: string; sourceUrl: string; title: string | null; fetchedAt: string };

export const getKnowledgeSources = () => request<KnowledgeSource[]>("/knowledge");

export const scrapeWebsite = (url: string) =>
  post<KnowledgeSource>("/knowledge/scrape", { url });

// ─── Availability ───────────────────────────────────────────────────────────

export type Slot = { resourceId: string; startsAt: Date; endsAt: Date; instant: Date };

export async function getAvailability(
  serviceId: string, from: Date, to: Date, timeZone: string,
): Promise<Slot[]> {
  const q = new URLSearchParams({ serviceId, from: from.toISOString(), to: to.toISOString() });
  const raw = await request<{ resourceId: string; startsAt: string; endsAt: string }[]>(
    `/availability?${q}`,
  );
  return raw.map(s => ({
    resourceId: s.resourceId,
    startsAt: toZonedWallClock(new Date(s.startsAt), timeZone),
    endsAt: toZonedWallClock(new Date(s.endsAt), timeZone),
    instant: new Date(s.startsAt),
  }));
}

// ─── Bookings ────────────────────────────────────────────────────────────────

export type Booking = {
  id: string;
  startsAt: Date;
  endsAt: Date;
  instant: Date;
  status: "confirmed" | "declined" | "cancelled" | "blocked";
  notes: string | null;
  priceMinor: number | null;
  currency: string | null;
  clientName: string | null;
  serviceName: string | null;
  personId: string | null;
  serviceId: string | null;
};

export async function getBookings(from: Date, to: Date, timeZone: string): Promise<Booking[]> {
  const q = new URLSearchParams({
    from: fromZonedWallClock(from, timeZone).toISOString(),
    to: fromZonedWallClock(to, timeZone).toISOString(),
  });
  const raw = await request<(Omit<Booking, "startsAt" | "endsAt" | "instant"> & { startsAt: string; endsAt: string })[]>(
    `/bookings?${q}`,
  );
  return raw.map(b => ({
    ...b,
    startsAt: toZonedWallClock(new Date(b.startsAt), timeZone),
    endsAt: toZonedWallClock(new Date(b.endsAt), timeZone),
    instant: new Date(b.startsAt),
  }));
}

export const addBooking = (input: {
  personId?: string;
  details?: { fullName: string; phone: string; email?: string };
  serviceId: string;
  slotInstant: Date;
  clientActionId: string;
  notes?: string;
}) =>
  post<{ bookingId: string; personId: string; status: string }>("/bookings", {
    personId: input.personId,
    details: input.details,
    serviceId: input.serviceId,
    startsAt: input.slotInstant.toISOString(),
    clientActionId: input.clientActionId,
    notes: input.notes,
  });

export const moveBooking = (id: string, startsAt: Date, timeZone: string, wasAt?: Date) =>
  post<{ id: string; startsAt: string; endsAt: string }>(
    `/bookings/${id}/move`,
    { startsAt: fromZonedWallClock(startsAt, timeZone, { near: wasAt }).toISOString() },
  );

export const setBookingStatus = (id: string, status: "confirmed" | "declined" | "cancelled") =>
  post<{ changed: boolean; status: string | null }>(`/bookings/${id}/status`, { status });

export const saveVisitNotes = (bookingId: string, notes: string) =>
  request<{ id: string; notes: string }>(`/bookings/${bookingId}/notes`, {
    method: "PUT", body: JSON.stringify({ notes }),
  });

// ─── Clients ─────────────────────────────────────────────────────────────────

export type Client = {
  id: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  bookings: number;
  lastVisit: string | null;
  nextVisit: string | null;
  lastService: string | null;
};

export const getClients = () => request<Client[]>("/clients");

export const addClient = (draft: { fullName: string; phone: string; email?: string }, clientActionId: string) =>
  post<{ id: string; fullName: string; phone: string | null; created: boolean }>(
    "/clients", { ...draft, clientActionId },
  );

export type ClientVisit = {
  id: string; startsAt: Date; endsAt: Date; status: string;
  serviceName: string | null; priceMinor: number | null; currency: string | null; notes: string | null;
};

export type ClientDetail = {
  id: string; fullName: string; phone: string | null; email: string | null;
  notes: string | null; archived: boolean; history: ClientVisit[];
};

export async function getClient(id: string, timeZone: string): Promise<ClientDetail> {
  const raw = await request<Omit<ClientDetail, "history"> & {
    history: (Omit<ClientVisit, "startsAt" | "endsAt"> & { startsAt: string; endsAt: string })[];
  }>(`/clients/${id}`);
  return {
    ...raw,
    history: raw.history.map(v => ({
      ...v,
      startsAt: toZonedWallClock(new Date(v.startsAt), timeZone),
      endsAt: toZonedWallClock(new Date(v.endsAt), timeZone),
    })),
  };
}

export const saveClient = (id: string, patch: Partial<{ fullName: string; phone: string; email: string | null; notes: string | null }>) =>
  request<{ changed: boolean; fields: string[] }>(`/clients/${id}`, {
    method: "PATCH", body: JSON.stringify(patch),
  });

// ═══ Personal-assistant surface (2026-10-19) ═══════════════════════════════
//
// PORTED from apps/memorial-hall/client/api.ts — calendar, alerts, groups,
// messages, to-do — with routes at the root rather than under /business, and
// the committee-only parts (tagging, attachments) left out. Duplication debt;
// see the server files for the plan.

// ─── The calendar ────────────────────────────────────────────────────────────

type WireItem = Omit<CalendarItem, "startsAt" | "endsAt"> & {
  startsAt: string; endsAt: string;
};

/**
 * Everything on between two moments.
 *
 * `failures` comes back rather than throwing. A calendar that renders empty
 * because a source failed is claiming nothing is on, and the screen has to be
 * able to say which part is missing instead.
 */
async function getCalendar(from: Date, to: Date, timeZone: string) {
  const q = new URLSearchParams({
    from: fromZonedWallClock(from, timeZone).toISOString(),
    to: fromZonedWallClock(to, timeZone).toISOString(),
  });
  const raw = await request<{
    items: WireItem[];
    failures: { source: string; error: string }[];
  }>(`/calendar?${q}`);
  return {
    items: raw.items.map(i => ({
      ...i,
      startsAt: toZonedWallClock(new Date(i.startsAt), timeZone),
      endsAt: toZonedWallClock(new Date(i.endsAt), timeZone),
    })),
    failures: raw.failures,
  };
}

/**
 * The calendar screen's transport, bound to the committee's clock.
 *
 * A factory rather than a constant, for the zone reason above. The result MUST
 * be memoised by the caller: `CalendarSurface` has it in its fetch
 * dependencies deliberately, so a fresh object each render is a fetch each
 * render.
 *
 * The occurrence ids carry an `@` and the colons of an ISO instant, so they
 * are encoded — unencoded they would be read as a different path.
 */
export const calendarPort = (timeZone: string): CalendarPort => ({
  list: (from, to) => getCalendar(from, to, timeZone),

  addEntry: entry => post<{ id: string }>("/calendar/entries", {
    title: entry.title,
    startsAt: fromZonedWallClock(entry.startsAt, timeZone).toISOString(),
    endsAt: fromZonedWallClock(entry.endsAt, timeZone).toISOString(),
    recurrence: entry.recurrence,
  }),

  skipOccurrence: id => post<{ changed: boolean }>(
    `/calendar/occurrences/${encodeURIComponent(id)}/skip`, {}),

  restoreOccurrence: id => post<{ changed: boolean }>(
    `/calendar/occurrences/${encodeURIComponent(id)}/restore`, {}),

  /** ONE occurrence, leaving the series alone. Deliberately a different call
   *  from `moveEntry`, not a flag on it: the one that could move a year of
   *  meetings should never be reachable by getting an argument wrong. */
  moveOccurrence: (id, startsAt, endsAt) => post<{ changed: boolean }>(
    `/calendar/occurrences/${encodeURIComponent(id)}/move`, {
      startsAt: fromZonedWallClock(startsAt, timeZone).toISOString(),
      endsAt: fromZonedWallClock(endsAt, timeZone).toISOString(),
    }),

  resetOccurrence: id => post<{ changed: boolean }>(
    `/calendar/occurrences/${encodeURIComponent(id)}/reset`, {}),

  moveEntry: (id, startsAt, endsAt) =>
    request<{ id: string }>(`/calendar/entries/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        startsAt: fromZonedWallClock(startsAt, timeZone).toISOString(),
        endsAt: fromZonedWallClock(endsAt, timeZone).toISOString(),
      }),
    }),
});

// ─── Alerts ──────────────────────────────────────────────────────────────────
//
// `@nulife/alarms`' store. One count behind every badge in this app, and the
// same one Documents will read when it lands — which is the whole point of the
// store being shared rather than each module keeping its own.

export type AlertItem = {
  id: string;
  resourceKind: string;
  resourceId: string;
  kind: string;
  title: string;
  body: string | null;
  /** Whether looking at it is enough, or a button has to be pressed. */
  needsAck: boolean;
  acknowledged: boolean;
  createdAt: Date;
  /** Mira's urgency tier, or null. */
  urgency: UrgencyTier | null;
};

export async function getAlerts(): Promise<{ waiting: number; alerts: AlertItem[] }> {
  const raw = await request<{
    waiting: number;
    alerts: (Omit<AlertItem, "createdAt"> & { createdAt: string })[];
  }>("/alerts");
  return {
    waiting: raw.waiting,
    alerts: raw.alerts.map(a => ({ ...a, createdAt: new Date(a.createdAt) })),
  };
}

/**
 * Say, explicitly, that this has been dealt with.
 *
 * Its own call, never folded into reading: "simply opening/viewing it does not
 * count as acknowledgement" — Drea, 2026-09-16. There is deliberately no
 * "acknowledge everything" here, because pressing one button to agree to an
 * unknown number of things is the opposite of an explicit action.
 */
export const acknowledgeAlert = (alertId: string) =>
  post<{ acknowledged: number }>("/alerts/acknowledge", { alertIds: [alertId] });

/** Mark them read. Always the caller's own — the server scopes it, and there
 *  is deliberately no parameter that could point at anybody else. */
export const markAlertsSeen = (alertIds?: string[]) =>
  post<{ cleared: number }>("/alerts/seen", alertIds ? { alertIds } : {});

// ─── Groups ──────────────────────────────────────────────────────────────────
//
// The tables and the domain functions have been here since the first
// migration; until tonight there was no route and no control. See
// `peopleApi.ts`'s Groups section.

export type GroupView = {
  id: string;
  name: string;
  /** How many people it actually reaches, counted from the membership rows.
   *  Zero is a real and important answer: an empty group cannot be messaged. */
  reaches: number;
  memberIds: string[];
};

export const getGroups = () => request<GroupView[]>("/people/groups");

/** Name it and fill it in one act — see the route's own comment for why the
 *  members are not a second call. */
export const createGroup = (name: string, memberIds: string[] = []) =>
  post<{ id: string; name: string; reaches: number }>(
    "/people/groups", { name, memberIds });

/** In or out. Answers with the reach the table holds afterwards, so a screen
 *  never has to guess what a toggle did. */
export const setGroupMember = (groupId: string, personId: string, member: boolean) =>
  post<{ reaches: number; memberIds: string[] }>(
    `/people/groups/${encodeURIComponent(groupId)}/members`,
    { personId, member });

// ─── Messages ────────────────────────────────────────────────────────────────

export type InboxThread = {
  threadId: string;
  /** A person, or a group. One inbox holds both. */
  kind: "direct" | "group";
  withName: string;
  /** Null on a group thread — there is no single other person. */
  withPersonId: string | null;
  /** Groups only: how many people the conversation reaches. */
  reaches: number | null;
  lastMessageAt: string | null;
  preview: string | null;
  unread: number;
};

export type ThreadMessage = {
  id: string;
  body: string;
  authorPersonId: string | null;
  authorName: string;
  mine: boolean;
  sentAt: string;
  editedAt: string | null;
  deleted: boolean;
  /**
   * How far your own message has got. Null on somebody else's.
   *
   * "sent" means the server has it — NOT that it reached their phone; there
   * is no device acknowledgement anywhere in this stack. "read" is real: it
   * comes from the other person's `lastReadAt`, the same marker the unread
   * badge counts from.
   */
  receipt: "sent" | "read" | null;
  /** Mira's urgency tier, or null. */
  urgency: UrgencyTier | null;
  /** Anything sent with it. `thumbnailUrl` is null for a file with no
   *  preview. */
  files: {
    id: string; filename: string; mimeType: string;
    url: string; thumbnailUrl: string | null; size: number;
  }[];
};

export type Conversation = {
  threadId: string;
  kind: "direct" | "group";
  withPersonId: string | null;
  withName: string | null;
  /** Groups only: how many people are in the thread. */
  reaches: number | null;
  messages: ThreadMessage[];
};

export const getInbox = () => request<InboxThread[]>("/messages");

export const getUnread = () =>
  request<{ unread: number; direct: number; threads: number }>("/messages/unread");

export const getConversation = (threadId: string) =>
  request<Conversation>(`/messages/thread/${encodeURIComponent(threadId)}`);

export const openConversationWith = (personId: string) =>
  post<{ threadId: string }>(`/messages/with/${encodeURIComponent(personId)}`, {});

/** Somebody or something a new conversation can be started with. */
export type MessageTarget = {
  kind: "person" | "group";
  id: string;
  name: string;
  /** Groups only: how many people are in it right now. */
  reaches?: number;
};

export const getMessageTargets = () =>
  request<MessageTarget[]>("/messages/targets");

/**
 * Open, or carry on, a group's conversation.
 *
 * Answers how many people it ACTUALLY reaches — counted from the thread's
 * participants after they were added, not from the group before. The screen
 * repeats that number rather than the one it asked for.
 */
export const openGroupConversation = (groupId: string) =>
  post<{ threadId: string; reaches: number }>(
    `/messages/group/${encodeURIComponent(groupId)}`, {});

/**
 * Send a message with files attached.
 *
 * Multipart so the text and the files arrive together — there is no moment
 * where the message exists and its pictures do not. `body` may be empty: the
 * server then uses the filenames, rather than inventing words nobody typed.
 */
export const sendMessage = (threadId: string, body: string) =>
  post<{ id: string; sentAt: string }>(
    `/messages/thread/${encodeURIComponent(threadId)}`, { body });

/**
 * Rewrite one of your own messages.
 *
 * `changed: false` is a real answer, not a failure: retyping the same words
 * is not an edit, no history row was written, and the screen must not claim
 * one. Anything that is actually refused — somebody else's message, one
 * already deleted — comes back as an error with the reason.
 */
export const editMessage = (messageId: string, body: string) =>
  request<{ changed: boolean; editedAt: string | null; edits?: number }>(
    `/messages/message/${encodeURIComponent(messageId)}`,
    { method: "PATCH", body: JSON.stringify({ body }) });

/** Take one of your own messages back. Soft: the thread keeps a gap saying
 *  so, and the history keeps what it said. `deleted: false` means it already
 *  was. */
export const deleteMessage = (messageId: string) =>
  request<{ deleted: boolean }>(
    `/messages/message/${encodeURIComponent(messageId)}`,
    { method: "DELETE" });

/** What a message said before. Fetched only when somebody asks for it —
 *  most messages are never edited. */
export type MessageVersion = {
  previousBody: string;
  editedAt: string;
  editorName: string;
};

export const getMessageHistory = (messageId: string) =>
  request<MessageVersion[]>(
    `/messages/message/${encodeURIComponent(messageId)}/history`);

// ─── Tasks ───────────────────────────────────────────────────────────────────

export type Task = {
  id: string;
  /** The discussion it was raised from, if any and if you can see it. */
  raisedIn?: { threadId: string; title: string } | null;
  title: string;
  description: string | null;
  status: "open" | "active" | "complete";
  priority: "low" | "medium" | "high" | null;
  dueAt: string | null;
  groupId: string | null;
  groupName: string | null;
  assignees: { personId: string; name: string; isLead: boolean }[];
  startedByName: string | null;
  completedByName: string | null;
  /** Done once, then put back. Only meaningful while `open`. */
  reopened: boolean;
  /** `name` is null when this app cannot name the thing — honest, unlike a
   *  uuid on screen. */
  links: { resourceKind: string; resourceId: string; role: string | null; name: string | null }[];
  /** Who this concerns. NOT who it is assigned to — see `assignees` for that,
   *  and the Tasks screen for why the two are deliberately different. */
  tags: {
    id: string; kind: "person" | "group"; subjectId: string; name: string;
    selfNominated: boolean;
  }[];
  /** You are tagged and have not taken it on. Turns "Take it" into "Accept". */
  taggedYou: boolean;
  /** Mira's urgency tier, or null. */
  urgency: UrgencyTier | null;
  noteCount: number;
  canRemove: boolean;
};

export type TaskNote = {
  id: string; body: string; kind: string; at: string; byName: string;
  /** The note this answers, or null for one that starts a thread. */
  replyToNoteId: string | null;
  byYou: boolean;
};
export type Linkable = { resourceKind: string; resourceId: string; name: string; group: string };

export const getTasks = (o: { mine?: boolean; status?: string; unassigned?: boolean } = {}) => {
  const q = new URLSearchParams();
  if (o.mine) q.set("mine", "1");
  if (o.status) q.set("status", o.status);
  if (o.unassigned) q.set("unassigned", "1");
  return request<Task[]>(`/tasks${q.toString() ? `?${q}` : ""}`);
};
export const getTaskNotes = (id: string) =>
  request<TaskNote[]>(`/tasks/${encodeURIComponent(id)}/notes`);
export const getTaskLinkables = () => request<Linkable[]>("/tasks/linkables");
export const createTask = (draft: {
  title: string; description?: string | null;
  priority?: string | null; dueAt?: string | null;
  links?: { resourceKind: string; resourceId: string }[];
}) => post<{ id: string }>("/tasks", draft);
export const removeTask = (id: string) =>
  post<{ changed: boolean }>(`/tasks/${encodeURIComponent(id)}/archive`, {});
export const takeTask = (id: string) =>
  post<{ changed: boolean }>(`/tasks/${encodeURIComponent(id)}/assign`, {});
export const startTask = (id: string, note?: string) =>
  post<{ started: boolean }>(`/tasks/${encodeURIComponent(id)}/start`, { note });
export const completeTask = (id: string) =>
  post<{ changed: boolean }>(`/tasks/${encodeURIComponent(id)}/complete`, {});
export const setTaskPriority = (id: string, priority: "high" | null) =>
  post<{ changed: boolean }>(`/tasks/${encodeURIComponent(id)}/priority`, { priority });
export const reopenTask = (id: string) =>
  post<{ changed: boolean }>(`/tasks/${encodeURIComponent(id)}/reopen`, {});
export const addTaskNote = (
  id: string, body: string, kind = "note", replyToNoteId: string | null = null,
) => post<{ id: string | null }>(
  `/tasks/${encodeURIComponent(id)}/note`, { body, kind, replyToNoteId });


// ─── Urgency ─────────────────────────────────────────────────────────────────
//
// Mira's four tiers, strongest first. Marking an item answers with what
// ACTUALLY happened — see the server's urgency.ts — and the screen reports
// exactly that, never a bare "done".

export const URGENCY_TIERS = ["mandatory", "urgent", "bit_urgent", "priority"] as const;
export type UrgencyTier = (typeof URGENCY_TIERS)[number];
export const URGENCY_LABEL: Record<UrgencyTier, string> = {
  mandatory: "Must be dealt with", urgent: "Urgent", bit_urgent: "A bit urgent", priority: "Priority",
};
export type UrgencyKind = "message" | "task" | "alert";

export type UrgencyOutcome = {
  tier: UrgencyTier | null;
  /** People who now have an in-app alert about it. */
  alerted: number;
  deliveries: { channel: "sms" | "push"; recipientName: string; status: "not_configured" | "sent" | "failed"; detail: string }[];
};

export const setUrgency = (kind: UrgencyKind, id: string, tier: UrgencyTier | null) =>
  tier === null
    ? send(`/urgency/${kind}/${encodeURIComponent(id)}`, { method: "DELETE" })
        .then(r => r.json() as Promise<UrgencyOutcome>)
    : request<UrgencyOutcome>(`/urgency/${kind}/${encodeURIComponent(id)}`, {
        method: "PUT", body: JSON.stringify({ tier }),
      });

// ─── Contacts ────────────────────────────────────────────────────────────────

export type Contact = {
  id: string; fullName: string; email: string | null; phone: string | null;
  /** Whether they have a login — i.e. whether anything sent to them can be read. */
  canSignIn: boolean; isYou: boolean;
  /** none / invited (link issued, not opened) / active. */
  signIn: "none" | "invited" | "active";
  /** Whether the owner may give this person a sign-in link. */
  linkable: boolean;
  groups: { id: string; name: string }[];
};
export const getContacts = () => request<Contact[]>("/people");
export const addContact = (draft: { fullName: string; phone?: string; email?: string }) =>
  post<{ id: string; fullName: string }>("/people", draft);

/** Counts for the top bar. Each is its own call so one failing leaves the
 *  other two honest — a failure shows NO badge, never a zero. */
export const getAlertCount = () => request<{ waiting: number }>("/alerts/count");

// ─── Capture (@nulife/capture-ui) ────────────────────────────────────────────

import { createCaptureClient } from "@nulife/capture-ui";
export const captureClient = createCaptureClient(client, "/captures");

// ─── Media (issue #16) ───────────────────────────────────────────────────────

import type { MediaPort, MediaItemView, MediaFolderView } from "@nulife/media-ui";

export const mediaPort = (): MediaPort => {
  const qs = (o: Record<string, string | undefined>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(o)) if (v) q.set(k, v);
    return q.toString() ? `?${q}` : "";
  };
  const bulk = (action: string, body: object) => post(`/media/items/${action}`, body);
  return {
    folders: async () => (await request<{ folders: MediaFolderView[] }>("/media/folders")).folders,
    items: f => request(`/media/items${qs({
      q: f.q, tag: f.tag, uploader: f.uploader, from: f.from, to: f.to, type: f.type,
      archived: f.archived ? "1" : undefined,
    })}`),
    upload: async (files, o) => {
      // Multipart, so through `send()` — see documentsPort.upload.
      const form = new FormData();
      if (o.folderId) form.append("folderId", o.folderId);
      if (o.date) form.append("date", o.date);
      if (o.tags?.length) form.append("tags", o.tags.join(","));
      for (const f of files) form.append("files", f, f.name);
      const res = await send("/media/items", { method: "POST", body: form });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new ApiError(res.status, (body as { error?: string })?.error ?? "Could not add that.");
      return (body as { items: MediaItemView[] }).items;
    },
    edit: async (ids, patch) => { await bulk("edit", { ids, ...patch }); },
    move: async (ids, folderId) => { await bulk("move", { ids, folderId }); },
    tag: async (ids, change) => { await bulk("tag", { ids, ...change }); },
    archive: async ids => { await bulk("archive", { ids }); },
    restore: async ids => { await bulk("restore", { ids }); },
    createFolder: async f => { await post("/media/folders", f); },
    updateFolder: async (id, patch) => { await request(`/media/folders/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) }); },
    archiveFolder: async id => { await request(`/media/folders/${encodeURIComponent(id)}`, { method: "DELETE" }); },
  };
};

// ─── The document library ────────────────────────────────────────────────────
//
// `DocumentsSurface` imports no API module; it is handed these. The shapes are
// the package's own, so nothing here re-describes a document — only how to
// reach one.

import type { DocumentsPort, DocumentView, LinkTarget } from "@nulife/documents-ui";

type WireDocument = Omit<DocumentView, "expiresAt" | "createdAt" | "updatedAt"> & {
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
};

const asDoc = (d: WireDocument): DocumentView => ({
  ...d,
  expiresAt: d.expiresAt ? new Date(d.expiresAt) : null,
  createdAt: new Date(d.createdAt),
  updatedAt: new Date(d.updatedAt),
});

/**
 * The library's transport.
 *
 * A factory so the caller can memoise it — the surface has it in its fetch
 * dependencies deliberately, and a fresh object each render is a fetch each
 * render.
 */
/** Library documents out of date or due soon, counted by the server now. */
export const getDocumentsNeedingAction = () =>
  request<{ count: number; expired: number; dueSoon: number }>("/documents/needs-action");

export const documentsPort = (
  opts: { canChangeCategories?: boolean } = {},
): DocumentsPort => ({
  categories: () => request("/documents/categories"),

  /** What the viewer should render, decided server-side — a Word document is
   *  only viewable once it has been converted, and the first call is what
   *  pays for that. */
  preview: id => request(`/documents/${id}/preview`),

  /** What is already in use, so the tag field can offer it rather than
   *  inviting somebody to retype it slightly differently. */
  tags: () => request("/documents/tags"),

  /** Public notes on a document (2026-10-03). The server answers only for a
   *  document this person can open. */
  notes: {
    list: id => request(`/documents/${encodeURIComponent(id)}/notes`),
    add: (id, body) => post(`/documents/${encodeURIComponent(id)}/notes`, { body }),
    remove: noteId => request(`/documents/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" }),
  },

  search: async filters => {
    const q = new URLSearchParams();
    if (filters.text) q.set("text", filters.text);
    // `null` means "not filed", which is a real filter and not an absence of
    // one — so it travels as the sentinel rather than being dropped.
    if (filters.categoryId === null) q.set("categoryId", "__unfiled__");
    else if (filters.categoryId) q.set("categoryId", filters.categoryId);
    if (filters.tag) q.set("tag", filters.tag);
    if (filters.mimePrefix) q.set("mimePrefix", filters.mimePrefix);
    if (filters.expiry) q.set("expiry", filters.expiry);
    const raw = await request<WireDocument[]>(
      `/documents${q.toString() ? `?${q}` : ""}`);
    return raw.map(asDoc);
  },

  upload: async input => {
    // Multipart, so through `send()` rather than `request()`: a multipart body
    // must not carry a JSON content-type, which `request()` always sets.
    const form = new FormData();
    form.append("file", input.file, input.file.name);
    if (input.title) form.append("title", input.title);
    if (input.description) form.append("description", input.description);
    if (input.categoryId) form.append("categoryId", input.categoryId);
    if (input.documentDate) form.append("documentDate", input.documentDate);
    if (input.expiresAt) form.append("expiresAt", input.expiresAt);
    if (input.tags?.length) form.append("tags", input.tags.join(","));
    // Always sent, even empty: the module replaces the set when the field is
    // present and leaves it alone when it is not, so omitting it would make
    // "about nothing" impossible to say.
    form.append("links", JSON.stringify(input.links ?? []));

    const res = await send("/documents", { method: "POST", body: form });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      throw new ApiError(res.status,
        (body as { error?: string })?.error ?? "Could not add that.");
    }
    return body as { id: string };
  },

  update: (id, patch) =>
    request(`/documents/${encodeURIComponent(id)}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),

  archive: id =>
    request(`/documents/${encodeURIComponent(id)}`, { method: "DELETE" }),

  linkables: () => request<LinkTarget[]>("/documents/linkables"),

  /**
   * The four together or not at all — half of this is worse than none.
   *
   * Absent entirely for a non-Admin, which is what makes the screen's courtesy
   * and the server's refusal agree: there is no control to press rather than a
   * control that fails.
   */
  categoryAdmin: opts.canChangeCategories ? {
    create: name => post("/documents/categories", { name }),
    rename: (id, name) =>
      request(`/documents/categories/${encodeURIComponent(id)}`, {
        method: "PATCH", body: JSON.stringify({ name }),
      }),
    archive: id =>
      request(`/documents/categories/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    reorder: ids =>
      request("/documents/categories/order", {
        method: "PUT", body: JSON.stringify({ ids }),
      }),
  } : undefined,
});


// ─── Social Media tile ───────────────────────────────────────────────────────
//
// Stage 1: draft three versions of one post and copy or download it. Nothing
// here posts anywhere.

export type SocialKind = "quick" | "poster" | "event" | "raffle" | "thanks" | "other";
export type SocialLook = "simple" | "friendly" | "bold";
export type SocialVersion = { look: SocialLook; title: string; line: string; text: string };
export type AmazingCredits = { allowance: number; used: number; left: number };
export type SocialBudget = { spentUsd: number; capUsd: number; remainingUsd: number; mode: "ai" | "basic"; amazing?: AmazingCredits & { available: boolean } };
export type AmazingTheme = "clean" | "warm" | "bold";
export const startAmazing = (d: { title: string; text: string; theme: AmazingTheme; photoId: string | null }) =>
  post<{ jobId: string; credits: AmazingCredits }>("/social/amazing", d);
export const getAmazing = (id: string) =>
  request<{ status: "running" } | { status: "failed"; error: string } | { status: "done"; result: SavedSocialPost & { credits: AmazingCredits } }>(`/social/amazing/${encodeURIComponent(id)}`);
export type SocialPhoto = { id: string; thumbUrl?: string; fullUrl: string; downloadUrl: string; title?: string | null };

export const getSocialBudget = () => request<SocialBudget>("/social/budget");
export const writeSocialDraft = (d: { kind: SocialKind; details: string; title?: string; hasImage: boolean }) =>
  post<{ versions: SocialVersion[]; mode: "ai" | "basic" }>("/social/drafts", { ...d, platform: "facebook" });
export const logSocialEvent = (action: "copied" | "downloaded" | "shared") =>
  post<{ ok: true }>("/social/events", { action }).catch(() => undefined);
export const getSocialPhotos = async (): Promise<SocialPhoto[]> =>
  (await request<{ items: SocialPhoto[] }>("/media/items?type=image")).items;
export const uploadSocialPhoto = async (file: File): Promise<SocialPhoto> => {
  const form = new FormData();
  form.append("files", file, file.name);
  const res = await send("/media/items", { method: "POST", body: form });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, (body as { error?: string })?.error ?? "Could not add that photo.");
  return (body as { items: SocialPhoto[] }).items[0]!;
};
export type SavedSocialPost = {
  id: string; folderId: string; folderTitle: string; fullUrl: string; thumbUrl: string; downloadUrl: string;
  truncated: boolean; emojiLeftOff: boolean;
};
export type SocialLayout = "classic" | "side" | "photofirst" | "poster" | "frame" | "card";
export type PhotoPlace = { x: number; y: number; zoom: number };
export type PictureInput = { title: string; text: string; layout: SocialLayout; photoId: string | null; photoX?: number; photoY?: number; photoZoom?: number };
export const saveSocialPost = (d: PictureInput) => post<SavedSocialPost>("/social/posts", d);
/** The picture as it would be saved, drawn but not kept: the live preview. */
export const previewSocialPost = async (d: PictureInput, signal?: AbortSignal): Promise<{ url: string; truncated: boolean; emojiLeftOff: boolean }> => {
  const res = await send("/social/preview", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d), signal });
  if (!res.ok) throw new ApiError(res.status, ((await res.json().catch(() => null)) as { error?: string } | null)?.error ?? "Could not draw the picture.");
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), truncated: res.headers.get("X-Truncated") === "true", emojiLeftOff: res.headers.get("X-Emoji-Left-Off") === "true" };
};

// ─── Reminders: a person's own lists ─────────────────────────────────────────

export type ReminderList = { id: string; name: string; open: number; urgent: number; total: number };
export type Reminder = {
  id: string; listId: string; title: string; note: string | null; tags: string[]; urgent: boolean;
  dueAt: string | null; state: "open" | "done" | "cleared";
  alertAt: string | null; alertSms: boolean; alertFired: boolean; smsStatus: string | null;
  /** Only on the Today / Overdue / Flagged / All views. */
  listName?: string;
};
export type SmartKind = "today" | "overdue" | "flagged" | "all";
/** Where the phone's day starts and ends, so "today" means the phone's today. */
const dayQuery = () => {
  const a = new Date(); a.setHours(0, 0, 0, 0);
  const b = new Date(a); b.setDate(b.getDate() + 1); b.setMilliseconds(-1);
  return `dayStart=${encodeURIComponent(a.toISOString())}&dayEnd=${encodeURIComponent(b.toISOString())}`;
};
export const getReminderOverview = () => request<Record<SmartKind, number>>(`/reminders/overview?${dayQuery()}`);
export const getReminderTags = () => request<{ tag: string; count: number }[]>("/reminders/tags");
export const getTaggedReminders = (tag: string) => request<Reminder[]>(`/reminders/tag/${encodeURIComponent(tag)}`);
export const getSmartReminders = (kind: SmartKind) => request<Reminder[]>(`/reminders/smart/${kind}?${dayQuery()}`);
export type ReminderDraft = {
  title: string; note?: string | null; tags?: string[]; urgent?: boolean; dueAt?: string | null;
  alertAt?: string | null; alertSms?: boolean;
};
export const getReminderOptions = () => request<{ sms: { bought: boolean; connected: boolean } }>("/reminders/options");
export const getReminderLists = () => request<ReminderList[]>("/reminders/lists");
export const createReminderList = (name: string) => post<{ id: string; name: string }>("/reminders/lists", { name });
export const renameReminderList = (id: string, name: string) =>
  request<{ id: string; name: string }>(`/reminders/lists/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ name }) });
export const removeReminderList = (id: string) =>
  request<{ removed: boolean }>(`/reminders/lists/${encodeURIComponent(id)}`, { method: "DELETE" });
export const getReminders = (listId: string, cleared = false) =>
  request<Reminder[]>(`/reminders/lists/${encodeURIComponent(listId)}/items${cleared ? "?cleared=1" : ""}`);
export const addReminder = (listId: string, draft: ReminderDraft) =>
  post<Reminder>(`/reminders/lists/${encodeURIComponent(listId)}/items`, draft);
export const changeReminder = (id: string, patch: Partial<ReminderDraft> & { listId?: string }) =>
  request<Reminder>(`/reminders/items/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
export const setReminderState = (id: string, state: Reminder["state"]) =>
  post<Reminder>(`/reminders/items/${encodeURIComponent(id)}/state`, { state });
export const deleteReminder = (id: string) =>
  request<{ removed: boolean }>(`/reminders/items/${encodeURIComponent(id)}`, { method: "DELETE" });
